import React, { useEffect, useRef, useState } from 'react';

// DESIGN V2 — the ONE header every tool page uses (Loudness, Metadata,
// Organize, Compatibility, Cues & Loops, Migrate, Library).
//
//   [ source chip ▾ ]  stats sentence            [⚙ Settings] [ghost] [PRIMARY]
//
// Exactly one `primary` action per screen: the next step. `source` is the
// picker chip (folder / playlist / all tracks). `stats` is an inline sentence
// of <b>numbers</b> — never tiles. `settings` (a React node) opens in a popover
// from the gear, so the list gets the full width.
export default function ToolHeader({ source, stats, actions, settings, settingsLabel = 'Settings', children, className = '' }) {
    const [open, setOpen] = useState(false);
    const wrapRef = useRef(null);
    useEffect(() => {
        if (!open) return undefined;
        const onDown = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
        const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
        document.addEventListener('mousedown', onDown);
        document.addEventListener('keydown', onKey);
        return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
    }, [open]);

    return (
        <div className={`v2-tool-header ${className}`}>
            {source && <div className="v2-tool-source">{source}</div>}
            {stats && <div className="v2-tool-stats v2-stat">{stats}</div>}
            {children}
            <div className="v2-tool-spacer" />
            {settings && (
                <div className="v2-tool-settings" ref={wrapRef}>
                    <button type="button" className={`v2-btn quiet${open ? ' on' : ''}`} onClick={() => setOpen((o) => !o)} aria-expanded={open}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" /></svg>
                        {settingsLabel}
                    </button>
                    {open && <div className="v2-popover" role="dialog">{settings}</div>}
                </div>
            )}
            {actions && <div className="v2-tool-actions">{actions}</div>}
        </div>
    );
}

// Small helpers so pages don't re-invent the pieces.
export function SourceChip({ icon, label, count, onClick, title }) {
    return (
        <button type="button" className="v2-btn v2-source-chip" onClick={onClick} title={title}>
            {icon}
            <span className="v2-source-label">{label}</span>
            {count != null && <span className="v2-faint v2-num">· {count}</span>}
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 12, height: 12, opacity: .6 }}><polyline points="6 9 12 15 18 9" /></svg>
        </button>
    );
}

export function PopoverRow({ label, hint, children }) {
    return (
        <div className="v2-popover-row">
            <div><div>{label}</div>{hint && <span className="v2-popover-hint">{hint}</span>}</div>
            <div>{children}</div>
        </div>
    );
}
